/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import '../../../style/components/MobileTable.scss';
import './MobileTransactionTables.scss';

import {
  string,
  object,
  number,
  oneOfType,
  arrayOf,
  element
} from 'prop-types';
import React, { Fragment } from 'react';
import cx from 'classnames';
import {
  flow,
  omit,
  reduce,
  isEmpty,
  isNil,
  kebabCase,
  map,
  startCase
} from 'lodash';

import TransactionInfoModal from 'components/layouts/Transaction/TransactionInfoModal';

import { Dollar, FormattedDate, Quantity } from 'components/shared/Text/Text';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

import { getHistoricalTransactions } from 'services/Rest';

import {
  isActiveAdvisory,
  isActivePlus,
  isBrokerage,
  isCollegeSavings,
  MUTUAL_FUND
} from 'services/Products';

import Toggle from 'components/shared/ToggleRP';

import {
  labelCells,
  sortObjectByKeys,
  valueCells
} from 'components/shared/Table/MobileTableSupport';

import { TRANSACTION_DETAILS } from 'services/Transactions';
import ViewFeeDetailsModal from 'components/layouts/Transaction/ViewFeeDetailsModal';
import Content from 'services/Content';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';

const MAX_DAYS = 1095;

const COL_LABEL_TYPE = 'type';
const COL_LABEL_AMOUNT = 'amount';

const columns = [COL_LABEL_TYPE, COL_LABEL_AMOUNT];

const historyTableSortOrder = [
  'tradeDate',
  'transactionDescription',
  'accountName',
  'accountLabel',
  'ticker',
  'accountWChecksum',
  'owners',
  'quantity',
  'price',
  'netDollarAmount',
  'costBasis'
];
const historyTableComparator = (a, b) =>
  historyTableSortOrder.indexOf(a) - historyTableSortOrder.indexOf(b);

const labelCellOverrides = {
  accountName: () => 'Account Name',
  accountWChecksum: () => 'Account Number',
  accountLabel: () => 'Account Label',
  costBasis: () => 'Cost Basis',
  netDollarAmount: () => 'Net Dollar Amount',
  owners: () => 'Owners',
  price: () => 'Price',
  quantity: () => 'Quantity',
  ticker: () => 'Account Ticker',
  tradeDate: () => 'Date',
  transactionDescription: () => 'Description',
  default: () => null
};

const collegeSavingsLabelCellOverrides = {
  accountName: () => 'Account Name',
  accountWChecksum: () => 'Account Number',
  accountLabel: () => 'Account Label',
  costBasis: () => 'Cost Basis',
  netDollarAmount: () => 'Transaction Amount',
  owners: () => 'Owners',
  price: () => 'Price',
  quantity: () => 'Unit Amount',
  ticker: () => 'Account Ticker',
  tradeDate: () => 'Date',
  transactionDescription: () => 'Description',
  default: () => null
};

const AccountWChecksum = ({ accountWChecksum }) => (
  <Fragment>{accountWChecksum}</Fragment>
);

const Owners = ({ cellValueClass, owners }) => (
  <td className={cellValueClass}>{owners}</td>
);

const AccountWChecksumWithPermissions = WithUserPermissions(AccountWChecksum);
const OwnersWithPermissions = WithUserPermissions(Owners);

const valueCellOverrides = (cellValueClass) => ({
  accountName: (accountName) => (
    <td className={cellValueClass}>{accountName}</td>
  ),

  accountWChecksum: (accountWChecksum) => (
    <td className={cellValueClass}>
      <AccountWChecksumWithPermissions
        accountWChecksum={accountWChecksum}
        shouldMask
      />
    </td>
  ),

  costBasis: (costBasis) => <td className={cellValueClass}>{costBasis}</td>,

  netDollarAmount: (netDollarAmount) => (
    <Dollar element="td" className={cellValueClass}>
      {netDollarAmount}
    </Dollar>
  ),

  owners: (owners) => (
    <OwnersWithPermissions
      cellValueClass={cellValueClass}
      owners={owners}
      shouldMask
    />
  ),

  price: (price) => (
    <Dollar element="td" className={cellValueClass}>
      {price}
    </Dollar>
  ),

  quantity: (quantity, { productType }) => (
    <Quantity
      element="td"
      extraDecimal={isBrokerage(productType) || isActivePlus(productType)}
      className={cellValueClass}>
      {quantity}
    </Quantity>
  ),

  ticker: (ticker) => (
    <td className={cellValueClass}>
      <div className="mobile-transaction-history-table-account-ticker">
        {ticker}
      </div>
    </td>
  ),

  tradeDate: (tradeDate) => (
    <FormattedDate element="td" className={cellValueClass}>
      {tradeDate}
    </FormattedDate>
  ),

  transactionDescription: (transactionDescription) => (
    <td className={cellValueClass}>{transactionDescription}</td>
  ),

  default: () => null
});

const TableColumns = ({ columns }) => (
  <tr className="columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={cx(
          `mobile-transaction-history-table-column-${kebabCase(name)}`,
          `e2e-mobile-transaction-history-table-column-${kebabCase(name)}`,
          'column-header-cell'
        )}
        key={name}>
        {startCase(name)}
      </th>
    ))}
  </tr>
);

TableColumns.propTypes = {
  columns: arrayOf(oneOfType([element, string]))
};

const detailExclusionList = ['productType', 'transactionType'];

const DetailCollection = ({ details, productType, hasCollegeSavingsOnly }) =>
  flow(
    (d) => omit(d, detailExclusionList),
    (d) => sortObjectByKeys(d, historyTableComparator),
    (d) =>
      reduce(
        d,
        mapDetailsToRows(details, productType, hasCollegeSavingsOnly),
        []
      )
  )(details);

const mapDetailsToRows =
  (accountDetails, productType, hasCollegeSavingsOnly) => (acc, value, key) =>
    acc.concat(
      DetailRow({
        value,
        name: key,
        accountDetails,
        productType,
        hasCollegeSavingsOnly
      })
    );

const rowLabelClass = (name) =>
  cx(
    'mobile-transaction-history-table-row-label',
    'e2e-mobile-transaction-history-table-row-label',
    `${name}-row-label`,
    `e2e-${name}-row-label`
  );

const rowValueClass = (name) =>
  cx(
    'mobile-transaction-history-table-row-value',
    'e2e-mobile-transaction-history-table-row-value',
    `${name}-row-value`,
    `e2e-${name}-row-value`
  );

const DetailRow = ({
  value,
  name,
  accountDetails,
  productType,
  hasCollegeSavingsOnly
}) => {
  const classLabel = rowValueClass(name);
  const labelMapper = labelCells({
    cellLabelClass: classLabel,
    overrides:
      isCollegeSavings(productType) || hasCollegeSavingsOnly
        ? collegeSavingsLabelCellOverrides
        : labelCellOverrides
  });

  const cellMapper = valueCells({
    cellValueClass: classLabel,
    overrides: valueCellOverrides(classLabel)
  });

  const rowClassName = `mobile-transaction-history-table-row e2e-${name}-row`;

  return (
    !isNil(value) && (
      <tr key={name} className={rowClassName}>
        <td className={rowLabelClass(name)}>
          {(labelMapper[name] || labelMapper.default)()}
        </td>
        {(cellMapper[name] || cellMapper.default)(value, accountDetails)}
      </tr>
    )
  );
};

DetailRow.propTypes = {
  value: oneOfType([string, number]),
  name: string,
  accountDetails: object
};

const AccountDetailsHeaderRow = ({ toggle, on, details }) => {
  const {
    accountName,
    productType,
    transactionDescription,
    transactionType,
    netDollarAmount
  } = details;

  const isRASFee =
    transactionType &&
    transactionType.toUpperCase() === 'OTHER FEE' &&
    accountName &&
    accountName.toUpperCase().includes('RETIREMENT ADVISORY SERVICE');

  return (
    <Fragment>
      <tr
        className="mobile-transaction-history-table-row type-label-header-row"
        onClick={toggle}>
        <td
          className={`mobile-transaction-history-table-type-label ${
            on ? 'expanded' : ''
          }`}
          colSpan={`${on ? 2 : 1}`}>
          {!isBrokerage(productType) &&
          !isActivePlus(productType) &&
          !isActiveAdvisory(productType)
            ? transactionDescription
            : isRASFee
            ? 'Retirement Advisory Service Fee'
            : transactionType}
        </td>

        {!on && (
          <Dollar
            element="td"
            className="mobile-transaction-history-table-amount-value">
            {netDollarAmount}
          </Dollar>
        )}
      </tr>
      {on && isRASFee && (
        <tr>
          <div className="mobile-transaction-history-table-ras-fee-modal">
            <ViewFeeDetailsModal
              detail={Content.retirementAdvisoryServiceFee}
            />
          </div>
        </tr>
      )}
    </Fragment>
  );
};

const TransactionInfoRow = ({ account }) => {
  const { productType, transactionDescription, transactionType } = account;
  let hasTransactionFeeModal;
  let transactionFeeModalDetail;

  if (isBrokerage(productType)) {
    hasTransactionFeeModal = TRANSACTION_DETAILS.includes(transactionType);
    transactionFeeModalDetail = transactionType;
  } else {
    hasTransactionFeeModal = TRANSACTION_DETAILS.includes(
      transactionDescription
    );
    transactionFeeModalDetail = transactionDescription;
  }

  if (hasTransactionFeeModal) {
    return (
      <tr className="mobile-transaction-history-table-row">
        <td className="mobile-transaction-history-table-row-label">
          <TransactionInfoModal
            linkText="Details"
            detail={transactionFeeModalDetail}
          />
        </td>
      </tr>
    );
  }

  return null;
};

const AccountRows = ({ account, productType, hasCollegeSavingsOnly }) => (
  <Toggle>
    {({on, toggle}) => (
      <Fragment>
        <AccountDetailsHeaderRow toggle={toggle} on={on} details={account}/>
        {on && <TransactionInfoRow account={account}/>}
        <AccountLabel accountId={account.accountId}/>
        {on && (
          <DetailCollection
            details={account}
            productType={productType}
            hasCollegeSavingsOnly={hasCollegeSavingsOnly}
          />
        )}
      </Fragment>
    )}
  </Toggle>
);

class MobileTransactionHistoryTable extends React.Component {
  componentDidMount() {
    getHistoricalTransactions(MAX_DAYS);
  }

  render() {
    const {
      groups,
      includeTransactionHistory,
      productType,
      hasCollegeSavingsOnly,
      viewMoreLink
    } = this.props;

    const noTransactionsMessage = includeTransactionHistory
      ? 'You currently do not have any Historical Transactions.'
      : 'There is no transaction history for the filter criteria selected.';

    return (
      <div className="mobile-transaction-history-table-container">
        {includeTransactionHistory && (
          <div className="account-details-historical-transactions">
            <h2 className="account-details-graph-header flex-header">
              Last Five Historical Transactions
              <a className="view-more-link" onClick={viewMoreLink}>
                View Transaction History
              </a>
            </h2>
          </div>
        )}
        {isEmpty(groups) ? (
          <div className="mobile-transaction-history-table-no-transactions">
            {noTransactionsMessage}
          </div>
        ) : (
          <table className="mobile-transaction-history-table">
            <thead className="mobile-transactions-header">
              <TableColumns columns={columns} />
            </thead>
            <tbody>
              {map(groups, (rows, date) => (
                <Fragment key={date}>
                  <tr className="mobile-transaction-history-table-row">
                    <FormattedDate
                      element="td"
                      className="mobile-transaction-history-table-trade-date"
                      colSpan={2}>
                      {date}
                    </FormattedDate>
                  </tr>
                  {rows.map((row, r) => (
                    <AccountRows
                      key={date + r}
                      account={row}
                      productType={productType}
                      hasCollegeSavingsOnly={hasCollegeSavingsOnly}
                    />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }
}

MobileTransactionHistoryTable.defaultProps = {
  includeTransactionHistory: false,
  productType: MUTUAL_FUND,
  viewMoreLink: '',
  hasCollegeSavingsOnly: false
};

export default MobileTransactionHistoryTable;
